import React, { useRef, useState, useEffect } from 'react';


// Helper component to dynamically scale font size for long keys

export const AutoScalingKey = ({ text }) => {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (containerRef.current && textRef.current) {
      const containerWidth = containerRef.current.offsetWidth - 8; 
      const textWidth = textRef.current.scrollWidth;
      
      if (textWidth > containerWidth) {
        setScale(Math.max(0.65, containerWidth / textWidth)); 
      } else {
        setScale(1);
      }
    }
  }, [text]);

  return (
    <div ref={containerRef} className="w-44 flex-shrink-0 overflow-hidden flex items-center h-5">
      <span 
        ref={textRef}
        className="font-bold text-gray-800 whitespace-nowrap origin-left inline-block"
        style={{ transform: `scale(${scale})`, fontSize: '0.875rem' }}
      >
        {text}:
      </span>
    </div>
  );
};

// Smart Image Component to handle subspecies fallbacks
export const TaxonImage = ({ taxId, taxName, expert=false }) => {
  const [validImgSrc, setValidImgSrc] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const testImageUrl = (url) => new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => resolve(url);
      img.onerror = reject;
      img.src = url;
    });

    const findAndLoadImage = async () => {
      if (!taxId && !taxName) return;

      // Prepare clean names
      const nameParts = taxName ? taxName.trim().split(/\s+/) : [];
      const binomialName = nameParts.length >= 2 ? `${nameParts[0]} ${nameParts[1]}` : taxName;
      const isSubspecies = nameParts.length > 1;

      // Strategy 1: Exact NCBI TaxID
      if (taxId) {
        try {
          const url = await testImageUrl(`https://api.ncbi.nlm.nih.gov/datasets/v2/taxonomy/taxon/${taxId}/image`);
          if (isMounted) return setValidImgSrc(url);
        } catch (e) {}
      }

      // Strategy 2: Parent NCBI (Subspecies Fallback)
      if (isSubspecies && binomialName) {
        try {
          const res = await fetch(`https://eutils.ncbi.nlm.nih.gov/entrez/eutils/esearch.fcgi?db=taxonomy&term=${encodeURIComponent(binomialName)}&retmode=json`);
          const data = await res.json();
          const parentTaxid = data.esearchresult?.idlist?.[0];
          
          if (parentTaxid) {
            const url = await testImageUrl(`https://api.ncbi.nlm.nih.gov/datasets/v2/taxonomy/taxon/${parentTaxid}/image`);
            if (isMounted) return setValidImgSrc(url);
          }
        } catch (e) {}
      }

      // Strategy 3: Wikipedia
      if (binomialName) {
        try {
          const res = await fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(binomialName)}`);
          if (res.ok) {
            const data = await res.json();
            if (data.thumbnail?.source) {
              const url = await testImageUrl(data.thumbnail.source);
              if (isMounted) return setValidImgSrc(url);
            }
          }
        } catch (e) {}
      }


      // Strategy 4: GBIF
      if (binomialName) {
        try {
          const matchRes = await fetch(`https://api.gbif.org/v1/species/match?name=${encodeURIComponent(binomialName)}`);
          const matchData = await matchRes.json();
          if (matchData.usageKey) {
            const mediaRes = await fetch(`https://api.gbif.org/v1/species/${matchData.usageKey}/media`);
            const mediaData = await mediaRes.json();
            const gbifImg = mediaData.results?.[0]?.identifier;
            if (gbifImg) {
              const url = await testImageUrl(gbifImg);
              if (isMounted) return setValidImgSrc(url);
            }
          }
        } catch (e) {}
      }

      // Strategy 5: genus level fallback
        if (isSubspecies && nameParts[0]) {
            try {
                const genusName = nameParts[0];
                const res = await fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(genusName)}`);
                if (res.ok) {
                    const data = await res.json();
                    if (data.thumbnail?.source) {
                        const url = await testImageUrl(data.thumbnail.source);
                        if (isMounted) return setValidImgSrc(url);
                    }
                }
            } catch (e) {}
        }
    }

    // Reset image when switching between samples
    setValidImgSrc(null); 
    findAndLoadImage();
    // Cleanup function
    return () => { isMounted = false; };
  }, [taxId, taxName]);

  // Don't render anything (not even an empty div) until we have a guaranteed working image
  if (!validImgSrc) return null;

  return (
    <img 
      src={validImgSrc} 
      alt={taxName || "Taxon"} 
      className={`max-h-56 sm:max-h-64 w-auto ${expert ? 'max-w-48' : 'max-w-96'} rounded-xl shadow-sm border border-gray-100`}
    />
  );
};

// Helper component for the tree-style row with aligned names
export const TaxonomyTreeItem = ({ rank, value, depth, colorScheme }) => {
  if (!value) return null;

  const colors = {
    host: {
      text: 'text-green-900',
      line: 'border-green-200',
      dot: 'bg-green-500',
      rank: 'text-green-600/60'
    },
    virus: {
      text: 'text-orange-900',
      line: 'border-orange-800/80',
      dot: 'bg-orange-600',
      rank: 'text-orange-700/60'
    }
  }[colorScheme];

  const indentStep = 7; // Staggering width
  const dotOffset = 4;    // Position of dot center

  return (
    <div className="flex items-center group h-5">
      {/* COLUMN 1: The Staircase Tree & Rank Labels */}
      {/* We set a fixed width (w-64) so the names in Column 2 align vertically */}
      <div className="relative w-40 shrink-0 h-full flex items-center">
        
        {/* Hierarchy Lines */}
        {depth > 0 && (
          <>
            {/* Vertical Line */}
            <div 
              className={`absolute top-0 h-[14px] border-l-2 ${colors.line}`} 
              style={{ left: `${(depth - 1) * indentStep + dotOffset}px` }}
            />
            {/* Horizontal "Elbow" Line */}
            <div 
              className={`absolute top-[12px] border-t-2 ${colors.line}`} 
              style={{ 
                left: `${(depth - 1) * indentStep + dotOffset}px`, 
                width: `${indentStep}px` 
              }}
            />
          </>
        )}

        {/* The Node Dot */}
        <div 
          className={`absolute w-2 h-2 rounded-full z-10 shadow-sm ${colors.dot}`} 
          style={{ left: `${depth * indentStep + 2}px`, top: '10px' }}
        />

        {/* The Rank Label (Staggered relative to dot) */}
        <span 
          className={`absolute font-bold text-[8px] uppercase tracking-[0.15em] whitespace-nowrap ${colors.rank}`}
          style={{ left: `${depth * indentStep + 18}px`, top: '10px' }}
        >
          {rank}
        </span>
      </div>

      {/* COLUMN 2: The Biological Name (Aligned vertically) */}
      <div className="flex-grow mt-4">
        <a 
          href={`https://www.ncbi.nlm.nih.gov/Taxonomy/Browser/wwwtax.cgi?name=${encodeURIComponent(value)}`}
          target="_blank" 
          rel="noopener noreferrer"
          className={`${colors.text} font-bold text-[0.65rem] hover:underline decoration-1 underline-offset-4 italic transition-colors whitespace-nowrap`}
        >
          {value}
        </a>
      </div>
    </div>
  );
};

export const TaxonomyTree = ({ data, type }) => {
  if (!data) return null;

  // Case-insensitive rank finder
  const getVal = (rankName) => {
    const searchTerms = [`${type}_${rankName}`, rankName];
    const actualKey = Object.keys(data).find(k => 
      searchTerms.some(term => k.toLowerCase() === term.toLowerCase())
    );
    return actualKey ? data[actualKey] : null;
  };
  
  const ranks = [
    { key: 'domain', label: 'Domain' },
    { key: 'superkingdom', label: 'Superkingdom' },
    { key: 'kingdom', label: 'Kingdom' },
    { key: 'phylum', label: 'Phylum' },
    { key: 'class', label: 'Class' },
    { key: 'order', label: 'Order' },
    { key: 'family', label: 'Family' },
    { key: 'genus', label: 'Genus' },
    { key: 'species', label: 'Species' }
  ];

  const activeItems = ranks
    .map(r => ({ 
        label: r.label, 
        value: getVal(r.key) 
    }))
    .filter(item => item.value);

  if (activeItems.length === 0) return null;

  return (
    <div className="my-5 ml-1">
      <h4 className={`text-[10px] font-black uppercase mb-0 border-b pb-1 inline-block ${type === 'host' ? 'text-green-800 border-green-100' : 'text-orange-800 border-orange-100'}`}>
        {type} Taxonomy
      </h4>
      <div className="flex flex-col">
        {activeItems.map((item, idx) => (
          <TaxonomyTreeItem 
            key={idx}
            rank={item.label}
            value={item.value}
            depth={idx} // This drives the indentation and line math
            colorScheme={type}
          />
        ))}
      </div>
    </div>
  );
};